/// Collapsible Fine-tune band between Archetype's preview and the
/// Behavior band. Closed by default — most users don't override axes,
/// so the controls are deferred behind a disclosure. Dirty state
/// (current axes != archetype baseline) surfaces a colored dot next
/// to the label and a "Reset to defaults" link in the open panel.

import { useState } from "react";
import type { AxisValues } from "../../api";
import { axisValuesEqual } from "../../lib/agent_defaults";
import {
  ASSERTIVENESS_OPTIONS,
  AxisPicker,
  EVIDENCE_OPTIONS,
  RESPONSIVENESS_OPTIONS,
} from "./AxisPicker";

export function FineTuneDisclosure({
  axes,
  baseline,
  colorToken,
  onAxesChange,
  onReset,
}: {
  axes: AxisValues;
  /** Archetype baseline (already register-overlaid by the api if it
   *  shipped post-overlay defaults; today this is the raw archetype
   *  baseline from the wire model). */
  baseline: AxisValues;
  colorToken: string;
  onAxesChange: (next: AxisValues) => void;
  onReset: () => void;
}) {
  const [open, setOpen] = useState(false);
  const dirty = !axisValuesEqual(axes, baseline);
  const summary = `· ${axes.assertiveness} · ${axes.responsiveness} · ${axes.evidence}`;

  return (
    <div style={{ marginTop: 14 }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "transparent",
          border: 0,
          cursor: "pointer",
          padding: 0,
          color: dirty ? "var(--ink-2)" : "var(--ink-4)",
          fontFamily: "inherit",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform .15s",
            color: dirty ? "var(--ink-2)" : "var(--ink-4)",
          }}
        >
          ▸
        </span>
        {dirty && (
          <span
            data-testid="fine-tune-dirty-dot"
            aria-label="Axes overridden from baseline"
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: `var(--${colorToken})`,
              display: "inline-block",
            }}
          />
        )}
        <span
          className="mono"
          style={{
            fontSize: 10.5,
            letterSpacing: ".08em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          Fine-tune
        </span>
        <span
          style={{
            fontSize: 11.5,
            fontStyle: dirty ? "normal" : "italic",
            color: dirty ? "var(--ink-2)" : "var(--ink-4)",
          }}
        >
          {summary}
        </span>
      </button>

      {open && (
        <div
          role="region"
          aria-label="Fine-tune axes"
          style={{
            marginTop: 12,
            padding: "14px 16px",
            borderRadius: 10,
            background: "var(--paper-2)",
            border: "1px solid var(--rule)",
            position: "relative",
          }}
        >
          {dirty && (
            <button
              type="button"
              onClick={onReset}
              style={{
                position: "absolute",
                top: 10,
                right: 12,
                background: "transparent",
                border: 0,
                padding: "3px 8px",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 11,
                color: "var(--ink-3)",
                borderRadius: 4,
              }}
            >
              ↺ Reset to defaults
            </button>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 16,
            }}
          >
            <AxisPicker
              label="Assertiveness"
              hint="How forcefully it puts its view forward."
              options={ASSERTIVENESS_OPTIONS}
              value={axes.assertiveness}
              onChange={(id) =>
                onAxesChange({
                  ...axes,
                  assertiveness: id as AxisValues["assertiveness"],
                })
              }
              colorToken={colorToken}
            />
            <AxisPicker
              label="Responsiveness"
              hint="How much it weighs the social side of the room."
              options={RESPONSIVENESS_OPTIONS}
              value={axes.responsiveness}
              onChange={(id) =>
                onAxesChange({
                  ...axes,
                  responsiveness: id as AxisValues["responsiveness"],
                })
              }
              colorToken={colorToken}
            />
            <AxisPicker
              label="Evidence"
              hint="How rigorously it grounds claims."
              options={EVIDENCE_OPTIONS}
              value={axes.evidence}
              onChange={(id) =>
                onAxesChange({
                  ...axes,
                  evidence: id as AxisValues["evidence"],
                })
              }
              colorToken={colorToken}
            />
          </div>
        </div>
      )}
    </div>
  );
}
